import { makeApi, Zodios, type ZodiosInstance, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const UserRole = z.enum(['admin', 'user', 'partner'])
const queryFilters = z
  .object({ role: UserRole, isActive: z.boolean(), isVerified: z.boolean() })
  .partial()
  .passthrough()
  .optional()
const Profession = z.enum([
  'data-scientist',
  'data-analyst',
  'data-engineer',
  'data-operations',
  'machine-learning-engineer',
  'software-engineer',
  'recruitment-manager',
  'other',
])
const PersonalInfo = z
  .object({
    country: z.string(),
    yearsOfXP: z.number().gte(0).lte(10),
    profession: Profession,
  })
  .partial()
  .passthrough()
const UserSettings = z
  .object({
    dismissedPersonalInfo: z.boolean(),
    allowsAnonymousExposure: z.boolean(),
    dismissedCandidateBetaOffer: z.boolean(),
    dismissedRodeoWorkspaceVideo: z.boolean(),
    feedbackLanguage: z.enum(['en', 'he', 'es', 'fr', 'de']),
    releaseNotesLastReadDate: z.string().datetime({ offset: true }),
  })
  .partial()
  .passthrough()
const UserSource = z.enum([
  'organic',
  'invited',
  'partnerCandidate',
  'partnerStudent',
  'publicCandidate',
  'publicStudent',
])
const UserBadge = z.enum([
  'assignmentOnly',
  'alphaTester',
  'hardcoreUser',
  'xplPython2',
])
const User = z
  .object({
    id: z.string(),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    avatar: z.string().optional(),
    role: UserRole,
    isActive: z.boolean(),
    isVerified: z.boolean(),
    emailVerified: z.boolean().optional(),
    accessGroups: z.array(z.string()),
    invitationId: z.string().optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    lastLoginAt: z.string().datetime({ offset: true }).optional(),
    notes: z.string().optional(),
    personalInfo: PersonalInfo.optional(),
    organizationId: z.string().optional(),
    personId: z.string().optional(),
    settings: UserSettings.optional(),
    source: UserSource.optional(),
    badges: z.array(UserBadge).optional(),
    authProvider: z.enum(['local', 'google', 'linkedin']).optional(),
  })
  .passthrough()
const RegisterRequest = z
  .object({
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    password: z.string().min(8),
    confirmPassword: z.string().min(8),
    source: UserSource.optional(),
    assignmentToken: z.string().optional(),
  })
  .passthrough()
const Person = z
  .object({
    id: z.string(),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    userId: z.string().optional(),
    invitationIds: z.array(z.string()),
    invitationToken: z.string().optional(),
    managingOrganizationIds: z.array(z.string()),
    ongoingAssignments: z.array(z.string()).optional(),
    onboardingStatus: z.enum(['added', 'invited', 'joined']),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .passthrough()
const OrganizationType = z.enum(['company', 'school'])
const AuthResponse = z
  .object({
    user: User,
    person: Person.optional(),
    accessToken: z.string(),
    expiresIn: z.number(),
    organizationType: OrganizationType.optional(),
  })
  .passthrough()
const RegisterResponse = z
  .object({
    result: z.enum(['completed', 'verificationRequired']),
    email: z.string().email(),
    authResponse: AuthResponse.optional(),
  })
  .passthrough()
const UserListFilter = z
  .object({
    role: z.array(UserRole),
    isActive: z.boolean(),
    isVerified: z.boolean(),
    accessGroups: z.array(z.string()),
    organizationId: z.array(z.string()),
    search: z.string(),
  })
  .partial()
  .passthrough()
const UserSortFieldEnum = z.enum([
  'id',
  'email',
  'firstName',
  'lastName',
  'role',
  'isActive',
  'isVerified',
  'createdAt',
  'updatedAt',
  'lastLoginAt',
  'organizationId',
])
const SortDirectionEnum = z.enum(['asc', 'desc'])
const ListUsersRequest = z
  .object({
    filter: UserListFilter,
    sortField: UserSortFieldEnum,
    sortDirection: SortDirectionEnum,
    limit: z.number().int().gte(1),
    offset: z.number().int().gte(0),
  })
  .partial()
  .passthrough()
const ErrorResponse = z
  .object({
    error: z.string(),
    errorCode: z
      .enum([
        'unverifiedEmail',
        'invalidCredentials',
        'uninvitedEmail',
        'userNotFound',
        'invalidToken',
        'expiredToken',
        'badRequest',
        'acceptedToken',
        'alreadyJoinedPublicAssignment',
        'userAlreadyExists',
        'wrongAuthProvider',
      ])
      .optional(),
  })
  .passthrough()
const AdminUserChallenge = z
  .object({
    challengeId: z.string(),
    taskId: z.string(),
    taskName: z.string().optional(),
    slug: z.string(),
    status: z.string(),
    startTime: z.string().datetime({ offset: true }),
    endTime: z.string().datetime({ offset: true }).optional(),
    feedbackGrade: z.number().optional(),
    feedbackStatus: z.string().optional(),
    assignmentId: z.string().optional(),
    deleted: z.boolean().optional(),
    assignmentDeleted: z.boolean().optional(),
  })
  .passthrough()
const AdminUserAssignment = z
  .object({
    assignmentId: z.string(),
    taskId: z.string(),
    organizationId: z.string(),
    organizationName: z.string().optional(),
    status: z.string(),
    startDate: z.string().datetime({ offset: true }).optional(),
    dueDate: z.string().datetime({ offset: true }).optional(),
    overdueDate: z.string().datetime({ offset: true }).optional(),
    submissions: z.number().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    taskName: z.string().optional(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    linkToken: z.string().optional(),
  })
  .passthrough()
const AdminUserDetailsResponse = z
  .object({
    user: User,
    challenges: z.array(AdminUserChallenge),
    assignments: z.array(AdminUserAssignment),
  })
  .passthrough()
const UpdateUserRequest = z
  .object({
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    role: UserRole,
    isActive: z.boolean(),
    isVerified: z.boolean(),
    accessGroups: z.array(z.string()),
    avatar: z.string(),
    notes: z.string(),
    personalInfo: PersonalInfo,
    organizationId: z.string(),
    settings: UserSettings,
    source: UserSource,
    badges: z.array(UserBadge),
  })
  .partial()
  .passthrough()
const LoginRequest = z
  .object({
    email: z.string().email(),
    password: z.string(),
    rememberMe: z.boolean().optional().default(false),
  })
  .passthrough()
const ForgotPasswordRequest = z
  .object({ email: z.string().email() })
  .passthrough()
const ResetPasswordRequest = z
  .object({
    token: z.string(),
    newPassword: z.string().min(8),
    confirmPassword: z.string().min(8),
  })
  .passthrough()
const VerifyEmailRequest = z.object({ token: z.string() }).passthrough()
const ResendVerificationRequest = z
  .object({ email: z.string().email() })
  .passthrough()
const queryFilters__2 = z
  .object({
    status: z.enum(['active', 'accepted', 'expired', 'revoked']),
    organizationId: z.string(),
  })
  .partial()
  .passthrough()
  .optional()
const Invitation = z
  .object({
    id: z.string(),
    email: z.string().email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    inviterName: z.string().optional(),
    inviterId: z.string().optional(),
    accessGroups: z.array(z.string()),
    validFor: z.number(),
    registeredUserId: z.string().optional(),
    status: z.enum(['active', 'accepted', 'expired', 'revoked']),
    role: UserRole.optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    expiresAt: z.string().datetime({ offset: true }),
    customBody: z.string().optional(),
    organizationId: z.string().optional(),
    tokenHash: z.string(),
    token: z.string().optional(),
  })
  .passthrough()
const CreateInvitationRequest = z
  .object({
    email: z.string().email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    inviterId: z.string().optional(),
    inviterName: z.string().optional(),
    accessGroups: z.array(z.string()),
    validFor: z.number().gte(1).lte(365),
    customBody: z.string().optional(),
    organizationId: z.string().optional(),
    sendEmails: z.boolean().optional(),
    role: UserRole.optional(),
  })
  .passthrough()
const ReferInvitationRequest = z
  .object({
    email: z.string().email(),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    message: z.string().max(500).optional(),
    sendEmail: z.boolean().optional(),
  })
  .passthrough()
const ReferInvitationResponse = z
  .object({
    invitation: Invitation,
    wasExisting: z.boolean(),
    inviteUrl: z.string().optional(),
  })
  .passthrough()
const MyInvitation = z
  .object({
    id: z.string(),
    email: z.string().email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    status: z.enum(['pending', 'accepted', 'expired']),
    createdAt: z.string().datetime({ offset: true }),
    expiresAt: z.string().datetime({ offset: true }),
    acceptedAt: z.string().datetime({ offset: true }).nullish(),
    registeredUserId: z.string().nullish(),
    inviteUrl: z.string().optional(),
  })
  .passthrough()
const MyInvitationsResponse = z.array(MyInvitation)
const OrganizationLogoCanvas = z.enum(['plaque', 'obsidian', 'vellum'])
const InvitationDetails = z
  .object({
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    inviterName: z.string(),
    role: UserRole,
    isPublicAssignment: z.boolean(),
    organizationId: z.string(),
    organizationName: z.string(),
    organizationLogoSquareURL: z.string(),
    organizationLogoURL: z.string(),
    organizationLogoCanvas: OrganizationLogoCanvas,
    taskName: z.string(),
    recipientHasAccount: z.boolean(),
    message: z.string(),
  })
  .partial()
  .passthrough()
const UpdateInvitationRequest = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
    accessGroups: z.array(z.string()),
    inviterId: z.string(),
    inviterName: z.string(),
    validFor: z.number().gte(1).lte(365),
    status: z.enum(['active', 'accepted', 'expired', 'revoked']),
    organizationId: z.string(),
    role: UserRole,
  })
  .partial()
  .passthrough()
const AdminDashboardResponse = z
  .object({
    totalUsers: z.number(),
    totalTasks: z.number(),
    completedChallenges: z.number(),
  })
  .passthrough()
const AdminDailyCountPoint = z
  .object({ date: z.string().datetime({ offset: true }), count: z.number() })
  .passthrough()
const AdminChallengesOverTimePoint = z
  .object({
    bucketStart: z.string().datetime({ offset: true }),
    startedCount: z.number(),
    submittedCount: z.number(),
  })
  .passthrough()
const AdminTaskOverviewEntry = z
  .object({
    taskId: z.string(),
    taskName: z.string(),
    challengesCount: z.number(),
    averageGrade: z.number(),
    gradeDistribution: z.array(z.number()),
  })
  .passthrough()
const AdminUserSegments = z
  .object({
    totalUsers: z.number(),
    regular: z.number(),
    candidates: z.number(),
    students: z.number(),
    partnerAdmins: z.number(),
    admins: z.number(),
    verified: z.number(),
    anonymousSharing: z.number(),
  })
  .passthrough()
const AdminConversionStats = z
  .object({
    candidatesTotal: z.number(),
    pending: z.number(),
    converted: z.number(),
    conversionRate: z.number(),
    notConvertedFinished: z.number(),
    ctaConversionRate: z.number(),
    startedSecondChallenge: z.number(),
    startedThirdChallenge: z.number(),
  })
  .passthrough()
const AdminActiveUsersPoint = z
  .object({
    date: z.string(),
    dau: z.number(),
    wau: z.number(),
    mau: z.number(),
  })
  .passthrough()
const AdminStatisticsResponse = z
  .object({
    userSignupsOverTime: z.array(AdminDailyCountPoint),
    challengesOverTimeDaily: z.array(AdminChallengesOverTimePoint),
    challengesOverTimeWeekly: z.array(AdminChallengesOverTimePoint),
    tasksOverview: z.array(AdminTaskOverviewEntry),
    userSegments: AdminUserSegments,
    conversionStats: AdminConversionStats,
    activeUsersOverTime: z.array(AdminActiveUsersPoint),
  })
  .passthrough()
const AnonymousSharingUser = z
  .object({
    userId: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string(),
    avatar: z.string().optional(),
    role: UserRole.optional(),
    country: z.string().optional(),
    yearsOfXP: z.number().optional(),
    profession: Profession.optional(),
    tasksStarted: z.number(),
    tasksFinished: z.number(),
    averageGrade: z.number().optional(),
  })
  .passthrough()
const AnonymousSharingUsersResponse = z
  .object({ users: z.array(AnonymousSharingUser) })
  .passthrough()
const AdminDeleteUserRequest = z.object({ dryRun: z.boolean() }).passthrough()
const AdminDeleteUserResponse = z
  .object({
    dryRun: z.boolean(),
    user: z.object({ id: z.string(), email: z.string() }).passthrough(),
    challenges: z.array(z.object({}).partial().passthrough()),
    assignments: z.array(z.object({}).partial().passthrough()),
    cohorts: z.array(z.object({}).partial().passthrough()),
    persons: z.array(z.object({}).partial().passthrough()),
    invitations: z.array(z.object({}).partial().passthrough()),
    sessions: z.object({ count: z.number() }).passthrough(),
    activityEvents: z.array(z.object({}).partial().passthrough()),
    deletedCounts: z
      .object({
        challenges: z.number(),
        assignments: z.number(),
        cohorts: z.number(),
        persons: z.number(),
        invitations: z.number(),
        sessions: z.number(),
        activityEvents: z.number(),
        user: z.number(),
      })
      .partial()
      .passthrough()
      .optional(),
  })
  .passthrough()
const HeardOfUsSource = z.enum([
  'instagram',
  'facebook',
  'linkedin',
  'reddit',
  'x',
  'chatGPT',
  'gemini',
  'claude',
  'google',
  'bing',
  'yandex',
  'email',
  'wordOfMouth',
  'other',
])
const WaitingListEntry = z
  .object({
    id: z.string(),
    email: z.string().email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    heardOfUs: HeardOfUsSource.optional(),
    heardOfUsOther: z.string().optional(),
    ipAddress: z.string().optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    hasInvitation: z.boolean(),
  })
  .passthrough()
const WaitingListGetResponse = z
  .object({ subscriptions: z.array(WaitingListEntry) })
  .passthrough()
const WaitingListRequest = z
  .object({
    email: z.string().email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    heardOfUs: HeardOfUsSource.optional(),
    heardOfUsOther: z.string().optional(),
    waitingListId: z.string().optional(),
  })
  .passthrough()
const WaitingListResponse = z
  .object({ waitingListId: z.string() })
  .passthrough()
const BulkInviteWaitingListRequest = z
  .object({
    waitingListIds: z.array(z.string()),
    accessGroups: z.array(z.string()),
    validFor: z.number().gte(1).lte(365).optional().default(30),
    customBody: z.string().optional(),
    role: UserRole.optional(),
    inviterId: z.string().optional(),
    inviterName: z.string().optional(),
  })
  .passthrough()
const BulkInviteResult = z
  .object({
    waitingListId: z.string(),
    email: z.string().email().optional(),
    success: z.boolean(),
    invitationId: z.string().optional(),
    error: z.string().optional(),
  })
  .passthrough()
const BulkInviteWaitingListResponse = z
  .object({
    totalRequested: z.number(),
    successful: z.number(),
    failed: z.number(),
    results: z.array(BulkInviteResult),
  })
  .passthrough()
const UpdatePersonalInfoRequest = z
  .object({
    country: z.string(),
    yearsOfXP: z.number().gte(0).lte(10),
    profession: Profession,
  })
  .partial()
  .passthrough()
const UpdateUserSettingsRequest = z
  .object({
    dismissedPersonalInfo: z.boolean(),
    allowsAnonymousExposure: z.boolean(),
    dismissedCandidateBetaOffer: z.boolean(),
    dismissedRodeoWorkspaceVideo: z.boolean(),
    feedbackLanguage: z.enum(['en', 'he', 'es', 'fr', 'de']),
    releaseNotesLastReadDate: z.string().datetime({ offset: true }),
  })
  .partial()
  .passthrough()
const CreatePersonRequest = z
  .object({
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    userId: z.string().optional(),
    organizationId: z.string(),
  })
  .passthrough()
const PersonDirectoryEntry = z
  .object({
    id: z.string(),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    userId: z.string().optional(),
    invitationIds: z.array(z.string()),
    managingOrganizationIds: z.array(z.string()),
    ongoingAssignments: z.array(z.string()).optional(),
    onboardingStatus: z.enum(['added', 'invited', 'joined']),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    joinedAt: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough()
const PersonInvitationTokenResponse = z
  .object({ invitationToken: z.string() })
  .partial()
  .passthrough()
const UpdatePersonRequest = z
  .object({
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    userId: z.string(),
    invitationIds: z.array(z.string()),
    managingOrganizationIds: z.array(z.string()),
    ongoingAssignments: z.array(z.string()),
  })
  .partial()
  .passthrough()
const DeletePersonResponse = z
  .object({ message: z.string() })
  .partial()
  .passthrough()
const SendAssignmentRequest = z
  .object({
    assignmentId: z.string(),
    personIds: z.array(z.string()),
    organizationId: z.string(),
    organizationName: z.string(),
    sendEmails: z.boolean(),
  })
  .passthrough()
const InvitationLink = z
  .object({
    personId: z.string(),
    email: z.string().email(),
    invitationId: z.string(),
    token: z.string().optional(),
    link: z.string().optional(),
  })
  .passthrough()
const SendAssignmentResponse = z
  .object({ message: z.string(), invitationLinks: z.array(InvitationLink) })
  .partial()
  .passthrough()
const JoinPublicAssignmentRequest = z
  .object({ assignmentToken: z.string() })
  .passthrough()
const JoinPublicAssignmentResponse = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
    personId: z.string(),
    assignmentId: z.string(),
  })
  .passthrough()
const AssignmentFeedbackData = z
  .object({
    org_grade: z.number().nullish(),
    org_feedback_md: z.string().nullish(),
    user_grade: z.number(),
    user_body: z.string(),
  })
  .passthrough()
const SendAssignmentSubmittedNotificationRequest = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().email(),
    startDate: z.string().datetime({ offset: true }),
    endDate: z.string().datetime({ offset: true }),
    workTime: z.number(),
    taskName: z.string(),
    organizationId: z.string(),
    feedback: AssignmentFeedbackData,
  })
  .passthrough()
const SendAssignmentSubmittedNotificationResponse = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
    emailsSent: z.number(),
  })
  .passthrough()
const ReleaseNoteCategory = z.enum([
  'feature',
  'bugfix',
  'challenge',
  'partner',
  'admin',
  'announcement',
  'comingsoon',
  'ai',
  'improvement',
])
const ReleaseNote = z
  .object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    body: z.string(),
    deletedAt: z.string().datetime({ offset: true }).optional(),
    noteDate: z.string().datetime({ offset: true }),
    labels: z.array(ReleaseNoteCategory),
    unread: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .passthrough()
const ReleaseNotesResponse = z
  .object({ notes: z.array(ReleaseNote), unreadCount: z.number() })
  .passthrough()
const CreateReleaseNoteRequest = z
  .object({
    slug: z.string(),
    title: z.string(),
    body: z.string(),
    noteDate: z.string().datetime({ offset: true }),
    labels: z.array(ReleaseNoteCategory),
    active: z.boolean().optional(),
  })
  .passthrough()
const UpdateReleaseNoteRequest = z
  .object({
    slug: z.string(),
    title: z.string(),
    body: z.string(),
    noteDate: z.string().datetime({ offset: true }),
    labels: z.array(ReleaseNoteCategory),
    active: z.boolean(),
  })
  .partial()
  .passthrough()
const SendEmailRequest = z
  .object({
    recipients: z.array(z.string().email()).min(1),
    subject: z.string().min(1),
    body: z.string().min(1),
    secretHash: z.string().min(1),
    cc: z.array(z.string().email()).optional(),
    bcc: z.array(z.string().email()).optional(),
  })
  .passthrough()
const SendEmailResponse = z
  .object({ success: z.boolean(), messageId: z.string() })
  .partial()
  .passthrough()
const UserSearchResult = z
  .object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
  })
  .passthrough()
const UserVoiceImage = z
  .object({
    data: z.string(),
    mimeType: z.enum(['image/png', 'image/jpeg', 'image/webp']),
  })
  .passthrough()
const UserVoiceRequest = z
  .object({
    topic: z.enum(['general', 'workspace', 'challenges', 'bug', 'feature']),
    message: z.string().min(1).max(3000),
    image: UserVoiceImage.optional(),
  })
  .passthrough()
const UserVoiceResponse = z.object({ ok: z.boolean() }).passthrough()
const InvitationSortFieldEnum = z.enum([
  'id',
  'email',
  'firstName',
  'lastName',
  'status',
  'createdAt',
  'updatedAt',
  'expiresAt',
  'organizationId',
  'role',
])
const WaitingListSortFieldEnum = z.enum([
  'id',
  'email',
  'firstName',
  'lastName',
  'heardOfUs',
  'createdAt',
  'updatedAt',
])

export const schemas = {
  UserRole,
  queryFilters,
  Profession,
  PersonalInfo,
  UserSettings,
  UserSource,
  UserBadge,
  User,
  RegisterRequest,
  Person,
  OrganizationType,
  AuthResponse,
  RegisterResponse,
  UserListFilter,
  UserSortFieldEnum,
  SortDirectionEnum,
  ListUsersRequest,
  ErrorResponse,
  AdminUserChallenge,
  AdminUserAssignment,
  AdminUserDetailsResponse,
  UpdateUserRequest,
  LoginRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyEmailRequest,
  ResendVerificationRequest,
  queryFilters__2,
  Invitation,
  CreateInvitationRequest,
  ReferInvitationRequest,
  ReferInvitationResponse,
  MyInvitation,
  MyInvitationsResponse,
  OrganizationLogoCanvas,
  InvitationDetails,
  UpdateInvitationRequest,
  AdminDashboardResponse,
  AdminDailyCountPoint,
  AdminChallengesOverTimePoint,
  AdminTaskOverviewEntry,
  AdminUserSegments,
  AdminConversionStats,
  AdminActiveUsersPoint,
  AdminStatisticsResponse,
  AnonymousSharingUser,
  AnonymousSharingUsersResponse,
  AdminDeleteUserRequest,
  AdminDeleteUserResponse,
  HeardOfUsSource,
  WaitingListEntry,
  WaitingListGetResponse,
  WaitingListRequest,
  WaitingListResponse,
  BulkInviteWaitingListRequest,
  BulkInviteResult,
  BulkInviteWaitingListResponse,
  UpdatePersonalInfoRequest,
  UpdateUserSettingsRequest,
  CreatePersonRequest,
  PersonDirectoryEntry,
  PersonInvitationTokenResponse,
  UpdatePersonRequest,
  DeletePersonResponse,
  SendAssignmentRequest,
  InvitationLink,
  SendAssignmentResponse,
  JoinPublicAssignmentRequest,
  JoinPublicAssignmentResponse,
  AssignmentFeedbackData,
  SendAssignmentSubmittedNotificationRequest,
  SendAssignmentSubmittedNotificationResponse,
  ReleaseNoteCategory,
  ReleaseNote,
  ReleaseNotesResponse,
  CreateReleaseNoteRequest,
  UpdateReleaseNoteRequest,
  SendEmailRequest,
  SendEmailResponse,
  UserSearchResult,
  UserVoiceImage,
  UserVoiceRequest,
  UserVoiceResponse,
  InvitationSortFieldEnum,
  WaitingListSortFieldEnum,
}

const endpoints: ReturnType<typeof makeApi> = makeApi([
  {
    method: 'get',
    path: '/',
    alias: 'get',
    description: `Retrieves all users with optional filtering, sorting and pagination`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'queryFilters',
        type: 'Query',
        schema: queryFilters,
      },
      {
        name: 'sortField',
        type: 'Query',
        schema: z
          .enum([
            'id',
            'email',
            'firstName',
            'lastName',
            'role',
            'isActive',
            'isVerified',
            'createdAt',
            'updatedAt',
            'lastLoginAt',
            'organizationId',
          ])
          .optional(),
      },
      {
        name: 'sortDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional(),
      },
    ],
    response: z.array(User),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/',
    alias: 'post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RegisterRequest,
      },
    ],
    response: RegisterResponse,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `User already exists`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/:id',
    alias: 'getId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: User,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/:id',
    alias: 'putId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateUserRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: User,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/:id',
    alias: 'deleteId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/admin/actions/delete-user/:id',
    alias: 'adminActionsDeleteUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ dryRun: z.boolean() }).passthrough(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AdminDeleteUserResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/admin/anonymous-sharing-users',
    alias: 'adminAnonymousSharingUsersGet',
    description: `Users with settings.allowsAnonymousExposure&#x3D;true plus per-user challenge counts and average grade. Admin only.`,
    requestFormat: 'json',
    response: AnonymousSharingUsersResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden - Admin access required`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/admin/dashboard',
    alias: 'getAdmindashboard',
    requestFormat: 'json',
    response: AdminDashboardResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden - Admin access required`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/admin/statistics',
    alias: 'getAdminstatistics',
    description: `Returns aggregated data for admin statistics charts: user signups, challenges over time, and task overview.`,
    requestFormat: 'json',
    response: AdminStatisticsResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden - Admin access required`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/admin/user-details/:id',
    alias: 'adminUserDetailsId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AdminUserDetailsResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/forgot-password',
    alias: 'postAuthforgotPassword',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string().email() }).passthrough(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/auth/google',
    alias: 'getAuthgoogle',
    requestFormat: 'json',
    response: z.void(),
    errors: [
      {
        status: 302,
        description: `Redirect to Google OAuth`,
        schema: z.void(),
      },
      {
        status: 400,
        description: `OAuth initiation failed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/auth/google/callback',
    alias: 'getAuthgooglecallback',
    requestFormat: 'json',
    parameters: [
      {
        name: 'code',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'state',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 302,
        description: `Redirect after successful authentication`,
        schema: z.void(),
      },
      {
        status: 400,
        description: `OAuth callback failed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/login',
    alias: 'postAuthlogin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoginRequest,
      },
    ],
    response: AuthResponse,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Invalid credentials`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/logout',
    alias: 'postAuthlogout',
    requestFormat: 'json',
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/refresh',
    alias: 'postAuthrefresh',
    requestFormat: 'json',
    response: AuthResponse,
    errors: [
      {
        status: 401,
        description: `Invalid refresh token`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/reset-password',
    alias: 'postAuthresetPassword',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ResetPasswordRequest,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Invalid or expired reset token`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/validate-token',
    alias: 'postAuthvalidateToken',
    requestFormat: 'json',
    response: AuthResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/verify-email',
    alias: 'postAuthverifyEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ token: z.string() }).passthrough(),
      },
    ],
    response: AuthResponse,
    errors: [
      {
        status: 400,
        description: `Invalid or expired verification token`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/internal/send-assignment-submitted-notification',
    alias: 'postInternalsendAssignmentSubmittedNotification',
    description: `Internal endpoint for sending email notifications to organization partners when a person submits an assignment solution and receives feedback. This endpoint should only be called by other backend services.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendAssignmentSubmittedNotificationRequest,
      },
    ],
    response: SendAssignmentSubmittedNotificationResponse,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/invitation/:token',
    alias: 'getInvitationToken',
    requestFormat: 'json',
    parameters: [
      {
        name: 'token',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: InvitationDetails,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Invitation not found`,
        schema: ErrorResponse,
      },
      {
        status: 409,
        description: `Already joined this public assignment`,
        schema: ErrorResponse,
      },
      {
        status: 410,
        description: `Invitation expired or inactive`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/invitation/:token',
    alias: 'putInvitationToken',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateInvitationRequest,
      },
      {
        name: 'token',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Invitation,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Invitation not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/invitation/:token/claim',
    alias: 'postInvitationTokenclaim',
    requestFormat: 'json',
    parameters: [
      {
        name: 'token',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Invitation,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Logged-in user&#x27;s email does not match the invitation email`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Invitation not found`,
        schema: ErrorResponse,
      },
      {
        status: 410,
        description: `Invitation expired, revoked, or already accepted`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/invitations',
    alias: 'getInvitations',
    description: `Retrieves all invitations with optional filtering, sorting and pagination`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'queryFilters',
        type: 'Query',
        schema: queryFilters__2,
      },
      {
        name: 'sortField',
        type: 'Query',
        schema: z
          .enum([
            'id',
            'email',
            'firstName',
            'lastName',
            'status',
            'createdAt',
            'updatedAt',
            'expiresAt',
            'organizationId',
            'role',
          ])
          .optional(),
      },
      {
        name: 'sortDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional(),
      },
    ],
    response: z.array(Invitation),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/invitations',
    alias: 'postInvitations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateInvitationRequest,
      },
    ],
    response: Invitation,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/invitations/mine',
    alias: 'listMyInvitations',
    requestFormat: 'json',
    response: z.array(MyInvitation),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/invitations/refer',
    alias: 'createReferral',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReferInvitationRequest,
      },
    ],
    response: ReferInvitationResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `User with this email already exists`,
        schema: z.void(),
      },
      {
        status: 429,
        description: `Rate limit exceeded`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/join-gamma',
    alias: 'postJoinGamma',
    requestFormat: 'json',
    response: User,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/join-public-assignment',
    alias: 'postJoinPublicAssignment',
    description: `Allows an existing authenticated user to join a public assignment. Creates a person record if one doesn&#x27;t exist for the user.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ assignmentToken: z.string() }).passthrough(),
      },
    ],
    response: JoinPublicAssignmentResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid or expired assignment token`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `User not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/list',
    alias: 'listUsers',
    description: `Retrieves users with a structured filter object plus pagination and sort. Admin only.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListUsersRequest,
      },
    ],
    response: z.array(User),
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/persons',
    alias: 'getPersons',
    requestFormat: 'json',
    parameters: [
      {
        name: 'personIds',
        type: 'Query',
        schema: z.array(z.string()).optional(),
      },
    ],
    response: z.array(Person),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/persons',
    alias: 'postPersons',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePersonRequest,
      },
    ],
    response: Person,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/persons/:id',
    alias: 'getPersonsId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Person,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Person not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/persons/:id',
    alias: 'putPersonsId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdatePersonRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Person,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Person not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'delete',
    path: '/persons/:id',
    alias: 'deletePersonsId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Person not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/persons/:id/invitation-token',
    alias: 'getPersonsIdinvitationToken',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.object({ invitationToken: z.string() }).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Person not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/persons/assign',
    alias: 'postPersonsassign',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendAssignmentRequest,
      },
    ],
    response: SendAssignmentResponse,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/persons/directory',
    alias: 'getPersonsdirectory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organizationId',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.array(PersonDirectoryEntry),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/release-notes',
    alias: 'getReleaseNotes',
    requestFormat: 'json',
    parameters: [
      {
        name: 'fullList',
        type: 'Query',
        schema: z.boolean().optional(),
      },
    ],
    response: ReleaseNotesResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/release-notes',
    alias: 'postReleaseNotes',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateReleaseNoteRequest,
      },
    ],
    response: ReleaseNote,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/release-notes/:id',
    alias: 'putReleaseNotesId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateReleaseNoteRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: ReleaseNote,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Release note not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/release-notes/:id',
    alias: 'deleteReleaseNotesId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Release note not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/release-notes/read',
    alias: 'postReleaseNotesread',
    requestFormat: 'json',
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/resend-verification',
    alias: 'postResendVerification',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string().email() }).passthrough(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `User already verified`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/search',
    alias: 'getSearch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'q',
        type: 'Query',
        schema: z.string().min(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().lte(50).optional().default(20),
      },
    ],
    response: z.array(UserSearchResult),
  },
  {
    method: 'post',
    path: '/send-email',
    alias: 'postSendEmail',
    description: `Sends an email with the specified subject, body, and optional attachments. Requires a valid secret hash for authorization.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendEmailRequest,
      },
    ],
    response: SendEmailResponse,
    errors: [
      {
        status: 400,
        description: `Bad request – missing or invalid fields`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden – invalid secret hash`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/update-personal-info',
    alias: 'putUpdatePersonalInfo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdatePersonalInfoRequest,
      },
    ],
    response: User,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `User not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/update-user-settings',
    alias: 'putUpdateUserSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateUserSettingsRequest,
      },
    ],
    response: User,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `User not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/user-voice',
    alias: 'submitUserVoice',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserVoiceRequest,
      },
    ],
    response: z.object({ ok: z.boolean() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid feedback payload`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Authentication required`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/waiting-list',
    alias: 'getWaitingList',
    description: `Retrieves all waiting list entries with optional sorting and pagination`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'sortField',
        type: 'Query',
        schema: z
          .enum([
            'id',
            'email',
            'firstName',
            'lastName',
            'heardOfUs',
            'createdAt',
            'updatedAt',
          ])
          .optional(),
      },
      {
        name: 'sortDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional(),
      },
    ],
    response: WaitingListGetResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden - Admin access required`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/waiting-list',
    alias: 'postWaitingList',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: WaitingListRequest,
      },
    ],
    response: z.object({ waitingListId: z.string() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 409,
        description: `Email already on waiting list`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/waiting-list/bulk-invite',
    alias: 'postWaitingListbulkInvite',
    description: `Sends invitations to multiple waiting list entries. Uses firstName/lastName from waiting list if available, otherwise sends invitation with generic wording.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkInviteWaitingListRequest,
      },
    ],
    response: BulkInviteWaitingListResponse,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: ErrorResponse,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden - Admin access required`,
        schema: ErrorResponse,
      },
    ],
  },
])

export const api: ZodiosInstance<typeof endpoints> = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions): ZodiosInstance<typeof endpoints> {
  return new Zodios(baseUrl, endpoints, options)
}
